import { datadogRum } from '@datadog/browser-rum';
import { getClient } from 'configcat-js';
import type { User } from './types/user';

async function getSessionSampleRate(configCatApiKey: string): Promise<number> {
  const configCatClient = getClient(configCatApiKey);
  return await configCatClient.getValueAsync('rumsamplerateconfiguration', 10);
}

export function initDatadog(
  envVariables: any,
  sampleRates: {
    sessionReplaySampleRate: number;
  },
  { uid, school: organizationId }: Partial<User>
): void {
  const {
    DATADOG_CLIENT_TOKEN,
    DATADOG_APPLICATION_ID,
    DATADOG_ENVIRONMENT,
    DATADOG_SERVICE,
    COMMIT_ID,
    CONFIG_CAT_KEY,
  } = envVariables;

  const { sessionReplaySampleRate } = sampleRates;
  getSessionSampleRate(CONFIG_CAT_KEY).then((conditionalSampleRate) => {
    datadogRum.init({
      clientToken: DATADOG_CLIENT_TOKEN,
      applicationId: DATADOG_APPLICATION_ID,
      // `site` refers to the Datadog site parameter of your organization
      // see https://docs.datadoghq.com/getting_started/site/
      site: 'datadoghq.eu',
      service: DATADOG_SERVICE,
      env: DATADOG_ENVIRONMENT,
      // Specify a version number to identify the deployed version of your application in Datadog
      version: COMMIT_ID,
      sessionSampleRate: conditionalSampleRate,
      sessionReplaySampleRate: sessionReplaySampleRate,
      traceSampleRate: 10,
      traceContextInjection: 'sampled',
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      useSecureSessionCookie: true,
      usePartitionedCrossSiteSessionCookie: true,
      allowedTracingUrls: [
        /https:\/\/.*\.innovamat\.com/,
        /https:\/\/(?!.*\.tools\.innovamat\.cloud).*\.innovamat\.cloud/,
      ],
      /*
      beforeSend: (event) => {
        if (
          event.type === 'error' &&
          event.error.message.includes('Ignored attempt to cancel')
        ) {
          return false;
        }
        // Ensure a boolean is always returned
        return true;
      }, */
    });

    datadogRum.setUser({
      id: uid,
      organization: organizationId,
    });
    // Init network detection issues
    networkDetection();
  });
}

export function networkDetection(): void {
  //Network detection
  let offlineTimestamp: number | null = null;
  let onlineTimestamp = null;
  const MINIMUM_OFFLINE_DURATION_IN_SECONDS = 5 * 60;
  // - offline
  window.addEventListener('offline', function () {
    offlineTimestamp = Date.now();
    datadogRum.addAction('network_status', {
      status: 'offline',
      timestamp: offlineTimestamp,
    });
  });

  // - online
  window.addEventListener('online', function () {
    onlineTimestamp = Date.now();
    datadogRum.addAction('network_status', {
      status: 'online',
      timestamp: onlineTimestamp,
    });

    if (offlineTimestamp) {
      const elapsedTimeInSeconds = (onlineTimestamp - offlineTimestamp) / 1000;
      if (elapsedTimeInSeconds >= MINIMUM_OFFLINE_DURATION_IN_SECONDS) {
        const error = new Error(
          `User lost connection. Time offline: ${elapsedTimeInSeconds} seconds`
        );
        error.name = 'ConnectionError';
        datadogRum.addError(error, {
          duration: elapsedTimeInSeconds,
        });
      }
      // Reset the offline timestamp
      offlineTimestamp = null;
    }
  });
}
